import React, { useState, useRef } from 'react';
import {
  Box, IconButton, InputBase, Tooltip, LinearProgress, Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as AttachIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { useChat } from '../../contexts/ChatContext';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import { useLanguage } from '../../contexts/LanguageContext';
import { chatTranslations } from './chatTranslations';

interface Props {
  conversationId: number;
}

const ALLOWED_EXTENSIONS = [
  '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp',
  '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
];

const MessageInput: React.FC<Props> = ({ conversationId }) => {
  const { sendMessage, sendFile } = useChat();
  const muiTheme = useMuiTheme();
  const { language } = useLanguage();
  const t = chatTranslations[language];
  const isRTL = language === 'ar';

  const [text, setText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setText(prev => prev + emojiData.emoji);
    setShowEmoji(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError(null);
    const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError(t.fileTypeNotAllowed);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    e.target.value = '';
  };

  const handleSend = async () => {
    if (!text.trim() && !selectedFile) return;
    try {
      setUploading(true);
      setFileError(null);
      if (selectedFile) {
        await sendFile(conversationId, selectedFile, text.trim() || undefined);
        setSelectedFile(null);
      } else {
        await sendMessage(conversationId, text.trim());
      }
      setText('');
      setShowEmoji(false);
    } catch (err: any) {
      setFileError(err?.response?.data?.message || t.failedToSend);
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !uploading && (!!text.trim() || !!selectedFile);
  const isDark = muiTheme.palette.mode === 'dark';

  return (
    <Box sx={{
      borderTop: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      position: 'relative',
      direction: isRTL ? 'rtl' : 'ltr',
      flexShrink: 0,
    }}>
      {uploading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}

      {/* Emoji Picker */}
      {showEmoji && (
        <Box sx={{
          position: 'absolute',
          bottom: '100%',
          [isRTL ? 'left' : 'right']: 8,
          zIndex: 1500,
          boxShadow: 4,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <EmojiPicker
            onEmojiClick={onEmojiClick}
            theme={isDark ? Theme.DARK : Theme.LIGHT}
            width={Math.min(300, window.innerWidth - 16)}
            height={340}
            lazyLoadEmojis
          />
        </Box>
      )}

      {/* File Preview */}
      {selectedFile && (
        <Box sx={{
          mx: 1.5, mt: 1,
          px: 1.5, py: 0.75,
          bgcolor: alpha(muiTheme.palette.primary.main, 0.08),
          border: '1px solid',
          borderColor: alpha(muiTheme.palette.primary.main, 0.2),
          borderRadius: 2,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1, flexShrink: 0,
            background: 'linear-gradient(135deg,#4facfe,#00f2fe)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AttachIcon sx={{ fontSize: 14, color: 'white' }} />
          </Box>
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }} noWrap>
            {selectedFile.name}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({(selectedFile.size / 1024).toFixed(0)} KB)
            </Typography>
          </Typography>
          <Tooltip title={t.removeFile}>
            <IconButton size="small" onClick={() => setSelectedFile(null)} sx={{ color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {fileError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', px: 1.5, pt: 0.5 }}>
          {fileError}
        </Typography>
      )}

      {/* Input Row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, px: 1, py: 1 }}>
        <Tooltip title={t.emoji}>
          <IconButton
            size="small"
            onClick={() => setShowEmoji(p => !p)}
            sx={{
              color: showEmoji ? 'primary.main' : 'text.secondary',
              transition: 'color 0.2s',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <EmojiIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t.attachFile}>
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <AttachIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* Text input */}
        <Box sx={{
          flex: 1,
          bgcolor: 'action.hover',
          borderRadius: 3,
          border: '1.5px solid',
          borderColor: 'divider',
          px: 1.5, py: 0.5,
          display: 'flex', alignItems: 'center',
          transition: 'border-color 0.2s',
          '&:focus-within': { borderColor: 'primary.main' },
        }}>
          <InputBase
            multiline
            maxRows={4}
            fullWidth
            placeholder={t.typeMessage}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={uploading}
            sx={{ fontSize: '0.85rem', direction: isRTL ? 'rtl' : 'ltr' }}
          />
        </Box>

        {/* Send button */}
        <Tooltip title={t.send}>
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              sx={{
                background: canSend
                  ? 'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)'
                  : 'transparent',
                color: canSend ? 'white' : 'text.disabled',
                width: 36, height: 36,
                transition: 'all 0.2s',
                '&:hover': {
                  background: canSend ? 'linear-gradient(135deg,#3d9be8 0%,#00d4e0 100%)' : 'transparent',
                  transform: canSend ? 'scale(1.05)' : 'none',
                },
                '&.Mui-disabled': { background: 'transparent' },
              }}
            >
              <SendIcon sx={{ fontSize: 17, transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default MessageInput;
